import { useParams, Link } from 'react-router-dom';
import { MOCK_BLOG_POSTS } from '@/lib/mock-api';
import { ArrowLeft, Clock, Calendar, User, Share2, BookmarkPlus } from 'lucide-react';
import { toast } from '@/components/ui/sonner';

const blogContent = {
  'introducing-clawstinger': {
    body: [
      { type: 'p', text: 'Today we\'re thrilled to announce the public beta of ClawStinger — managed hosting for OpenClaw, the open-source personal AI assistant. What used to take 60 minutes of SSH config, dependency hell, and server tuning now takes under 60 seconds.' },
      { type: 'h2', text: 'The Problem' },
      { type: 'p', text: 'OpenClaw is incredible software. It can read your email, schedule meetings, manage your calendar, draft documents, and automate virtually anything you throw at it. But getting it running? That\'s where most people give up.' },
      { type: 'p', text: 'You need a VPS. You need to install Python, Node.js, and a dozen dependencies. You need to configure your LLM API keys, set up a reverse proxy, connect your messaging app, and hope nothing breaks when you restart.' },
      { type: 'h2', text: 'Our Solution' },
      { type: 'p', text: 'ClawStinger eliminates all of that. Pick your AI model (Claude, GPT, Gemini), choose a messaging channel (Telegram, Discord, Slack, WhatsApp), and click Deploy. We handle everything else.' },
      { type: 'code', text: '$ clawstinger deploy --model claude-opus --channel telegram\n\nProvisioning VPS in us-east-1...\nInstalling OpenClaw v0.8.2...\nConfiguring Claude Opus 4.5...\nConnecting Telegram bot...\n\n✓ Your agent is live at 45.33.21.100\n✓ Total time: 47 seconds' },
      { type: 'h2', text: 'What You Get' },
      { type: 'list', items: ['Dedicated VPS with pre-installed OpenClaw', 'Browser-based RDP access for full desktop control', 'Daily automated backups', 'One-click skills marketplace with 100+ extensions', 'Real-time cost monitoring and analytics', 'Multi-agent support (up to 20 agents per instance)'] },
      { type: 'h2', text: 'Pricing' },
      { type: 'p', text: 'We offer three tiers: Starter ($12/mo), Professional ($29/mo), and Enterprise ($69/mo). All plans include a 14-day free trial. No credit card required to start.' },
      { type: 'p', text: 'We\'re excited to see what you build. Head to clawstinger.com to get started, and join our Discord community to share feedback.' },
    ],
  },
  'multi-agent-workflows': {
    body: [
      { type: 'p', text: 'One of ClawStinger\'s most powerful features is multi-agent support. Instead of running a single AI assistant, you can orchestrate multiple agents — each with their own personality, skills, and messaging channels.' },
      { type: 'h2', text: 'Why Multiple Agents?' },
      { type: 'p', text: 'Consider an agency managing 10 clients. Each client needs a customer support bot with different branding, knowledge bases, and response styles. With ClawStinger\'s Professional plan, you can run up to 10 agents on a single instance.' },
      { type: 'h2', text: 'Setting Up Multi-Agent' },
      { type: 'p', text: 'From your instance dashboard, navigate to Configuration and click "Add Agent". Each agent gets its own:' },
      { type: 'list', items: ['AI model selection (mix Claude, GPT, and Gemini)', 'Persona and system prompt', 'Skills and integrations', 'Messaging channel connection', 'Memory and context window', 'Usage analytics and cost tracking'] },
      { type: 'code', text: '# Agent 1: Customer Support (Telegram)\nagent:\n  name: support-bot\n  model: claude-opus-4.5\n  channel: telegram\n  persona: "Friendly customer support agent"\n\n# Agent 2: Dev Assistant (Discord)\nagent:\n  name: dev-helper\n  model: gpt-5.2\n  channel: discord\n  persona: "Technical developer assistant"' },
      { type: 'h2', text: 'Resource Sharing' },
      { type: 'p', text: 'All agents on an instance share the underlying compute resources. ClawStinger automatically load-balances across agents, ensuring fair resource allocation. If one agent is idle, others can use the spare capacity.' },
      { type: 'p', text: 'Monitor per-agent resource usage from the Analytics dashboard. Set per-agent budget caps to prevent runaway API costs.' },
    ],
  },
};

const defaultContent = {
  body: [
    { type: 'p', text: 'This article is coming soon. We\'re working on detailed content for this post. In the meantime, check out our other articles or join our Discord community for the latest updates.' },
    { type: 'p', text: 'Want to be notified when this article is published? Subscribe to our newsletter at the bottom of any page.' },
  ],
};

export default function BlogDetail() {
  const { slug } = useParams();
  const post = MOCK_BLOG_POSTS.find(p => p.slug === slug);
  const content = blogContent[slug] || defaultContent;

  if (!post) {
    return (
      <div className="bg-[#050505] min-h-screen pt-24">
        <div className="max-w-3xl mx-auto py-24 px-4 text-center">
          <h1 className="text-2xl font-mono text-white mb-4">Post Not Found</h1>
          <Link to="/blog" className="text-emerald-400 font-mono hover:underline">Back to Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#050505] min-h-screen pt-24">
      <article className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-2 text-sm font-mono text-zinc-500 hover:text-emerald-400 transition-colors mb-8">
            <ArrowLeft size={14} /> Back to Blog
          </Link>

          <div className="flex items-center gap-3 mb-4">
            <span className="bg-emerald-500/10 text-emerald-400 text-xs font-mono px-2 py-0.5 rounded-sm border border-emerald-500/20">{post.category}</span>
          </div>

          <h1 data-testid="blog-detail-title" className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white font-mono mb-6 leading-tight">{post.title}</h1>

          <div className="flex items-center gap-6 mb-10 pb-8 border-b border-zinc-800">
            <div className="flex items-center gap-2 text-xs font-mono text-zinc-500">
              <Calendar size={12} /> {post.date}
            </div>
            <div className="flex items-center gap-2 text-xs font-mono text-zinc-500">
              <Clock size={12} /> {post.readTime} read
            </div>
            <div className="flex items-center gap-2 text-xs font-mono text-zinc-500">
              <User size={12} /> ClawStinger Team
            </div>
            <div className="ml-auto flex gap-2">
              <button data-testid="blog-share-btn" onClick={() => { try { navigator.clipboard.writeText(window.location.href); toast.success('Link copied to clipboard'); } catch { toast.success('Share URL: ' + window.location.href); } }} className="p-2 border border-zinc-800 rounded-sm text-zinc-500 hover:text-emerald-400 hover:border-emerald-500/20 transition-all">
                <Share2 size={14} />
              </button>
              <button data-testid="blog-bookmark-btn" onClick={() => toast.success('Article bookmarked')} className="p-2 border border-zinc-800 rounded-sm text-zinc-500 hover:text-emerald-400 hover:border-emerald-500/20 transition-all">
                <BookmarkPlus size={14} />
              </button>
            </div>
          </div>

          {/* Article body */}
          <div className="space-y-6">
            {content.body.map((block, i) => {
              if (block.type === 'h2') return <h2 key={i} className="text-xl font-mono text-white font-bold mt-10 mb-2 section-marker">{block.text}</h2>;
              if (block.type === 'p') return <p key={i} className="text-sm text-zinc-400 leading-relaxed">{block.text}</p>;
              if (block.type === 'code') return (
                <div key={i} className="terminal-window my-6">
                  <div className="terminal-header">
                    <div className="terminal-dot bg-red-500/80" /><div className="terminal-dot bg-yellow-500/80" /><div className="terminal-dot bg-green-500/80" />
                  </div>
                  <pre className="terminal-body text-xs whitespace-pre-wrap">{block.text}</pre>
                </div>
              );
              if (block.type === 'list') return (
                <ul key={i} className="space-y-2 ml-4">
                  {block.items.map((item, j) => (
                    <li key={j} className="text-sm text-zinc-400 flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">&#x2022;</span> {item}
                    </li>
                  ))}
                </ul>
              );
              return null;
            })}
          </div>

          {/* Author + related */}
          <div className="mt-16 pt-8 border-t border-zinc-800">
            <div className="flex items-center gap-4 mb-10">
              <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-mono font-bold">CS</div>
              <div>
                <p className="text-sm font-mono text-white font-bold">ClawStinger Team</p>
                <p className="text-xs text-zinc-500">Building the future of managed AI hosting</p>
              </div>
            </div>

            <h3 className="text-lg font-mono text-white font-bold mb-4">Related Articles</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {MOCK_BLOG_POSTS.filter(p => p.slug !== slug).slice(0, 2).map(p => (
                <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`related-${p.id}`} className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-5 hover:border-zinc-700 transition-all group">
                  <span className="text-xs font-mono text-emerald-400">{p.category}</span>
                  <h4 className="text-sm font-mono text-white font-bold mt-2 group-hover:text-emerald-400 transition-colors">{p.title}</h4>
                  <p className="text-xs text-zinc-600 mt-2">{p.date} &middot; {p.readTime}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}
